import React, { useEffect, useState } from "react";
import { Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { FaSitemap, FaUser } from "react-icons/fa";
import { GetLanguageString, NoResult } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { applyClassToCharacters, LoadingSpinner } from "../../constants/const";
import { ApiFund } from "../funds/ApiFund";
import { viewUsraReceiptPdf } from "../shared/receiptViewer";
import { encryptStorage } from "../../constants/EncryptStorage";

const cellStyle = { padding: "12px 14px", color: "#676a6c", verticalAlign: "middle" };
const headerStyle = {
  padding: "12px 14px",
  color: "#2f4050",
  fontWeight: 700,
  fontSize: 12,
  background: "#f9fafb",
  borderBottom: "1px solid #e7eaec",
};

const SummaryCard = ({ label, title, value, icon }) => (
  <div
    style={{
      padding: "12px 14px",
      backgroundColor: "#ffffff",
      borderRadius: 6,
      border: "1px solid #e7eaec",
      borderLeft: "4px solid #1ab394",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
      height: "100%",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#6b7280",
            letterSpacing: "0.3px",
            marginBottom: 4,
          }}
        >
          {label}
          {title ? (
            <>
              {" ("}
              {applyClassToCharacters(title, "ur")}
              {")"}
            </>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#2f4050",
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {applyClassToCharacters(value || "-", "ur")}
        </div>
      </div>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: "#e8f8f5",
          color: "#1ab394",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
    </div>
  </div>
);

const UsraRemittanceApprovalForm = ({
  notificationId,
  setVisible,
  setRefreshList,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const isUrduUi = GetSelectedLanguage() === "ur";
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const responsibilityId = parseInt(encryptStorage.getItem("responsibilityId"));
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const receipts = detail?.receipts || [];
  const total = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const formatAmount = (value) => new Intl.NumberFormat().format(Number(value) || 0);
  const requesterTitle = detail?.requestedByTitle || t("quick_reports3_naqeeb");

  useEffect(() => {
    if (!notificationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    ApiFund()
      .GetUsraRemittanceDetail(notificationId)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [notificationId]);

  const close = () => {
    if (typeof setVisible === "function") setVisible(false);
  };

  const act = (code) => {
    if (saving) return;
    setSaving(true);
    ApiFund()
      .UsraRemittanceAction({
        code,
        notificationId,
        comments,
        responsibilityId,
        loggedInUnitId,
      })
      .then((res) => {
        if (res.data?.statusCode === "200") {
          close();
          if (setRefreshList) setRefreshList((x) => !x);
        } else {
          swal({
            title: t(res.data?.message || "usra_remittance_failed"),
            icon: "error",
            className: isUrduUi ? "ur" : "",
          });
        }
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "usra_remittance_failed";
        swal({
          title: t(msg),
          icon: "error",
          className: isUrduUi ? "ur" : "",
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal isOpen toggle={close} size="lg">
      <ModalHeader toggle={close}>
        <GetLanguageString props={readOnly ? "usra_remittance_details" : "usra_confirm_cash"} />
      </ModalHeader>
      <ModalBody>
        {loading ? (
          LoadingSpinner()
        ) : !detail ? (
          <NoResult />
        ) : (
          <>
            <Row style={{ marginLeft: -8, marginRight: -8, marginBottom: 8 }}>
              <Col md={6} sm={12} style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 12 }}>
                <SummaryCard
                  label={<GetLanguageString props="usra_requested_by" />}
                  title={requesterTitle}
                  value={detail.requestedByName}
                  icon={<FaUser size={14} />}
                />
              </Col>
              <Col md={6} sm={12} style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 12 }}>
                <SummaryCard
                  label={<GetLanguageString props="usra_name" />}
                  value={detail.usraName}
                  icon={<FaSitemap size={14} />}
                />
              </Col>
            </Row>
            {receipts.length === 0 ? (
              <NoResult />
            ) : (
              <Table
                responsive
                className="mb-0"
                style={{ background: "#fff", border: "1px solid #e7eaec" }}
              >
                <thead>
                  <tr>
                    <th style={headerStyle}>#</th>
                    <th style={headerStyle}>
                      <GetLanguageString props="funds_receipt_number" />
                    </th>
                    <th style={headerStyle}>
                      <GetLanguageString props="member_report_member_serialNumber" />
                    </th>
                    <th style={headerStyle}>
                      <GetLanguageString props="common_name" />
                    </th>
                    <th style={headerStyle}>
                      <GetLanguageString props="funds_add_fund_amount" />
                    </th>
                    <th style={headerStyle} />
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r, index) => (
                    <tr
                      key={r.id}
                      style={{
                        borderBottom: "1px solid #d9e2e1",
                        background: index % 2 === 0 ? "#ffffff" : "#e6f3ef",
                      }}
                    >
                      <td style={cellStyle}>{index + 1}</td>
                      <td style={cellStyle}>
                        {applyClassToCharacters(r.receiptNumber || "-", "ur")}
                      </td>
                      <td style={cellStyle}>{r.serialNumber || "-"}</td>
                      <td style={cellStyle}>
                        {applyClassToCharacters(r.payerName || "-", "ur")}
                      </td>
                      <td style={{ ...cellStyle, fontWeight: 600, color: "#2f4050" }}>
                        {formatAmount(r.amount)}
                      </td>
                      <td style={{ ...cellStyle, textAlign: isUrduUi ? "left" : "right" }}>
                        {r.receiptToken && (
                          <Button
                            size="sm"
                            color="secondary"
                            outline
                            onClick={() =>
                              viewUsraReceiptPdf({
                                receiptToken: r.receiptToken,
                                t,
                              })
                            }
                          >
                            <GetLanguageString props="common_view_receipt" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot style={{ backgroundColor: "#f3f3f4", borderTop: "2px solid #e7eaec" }}>
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        ...cellStyle,
                        textAlign: isUrduUi ? "left" : "right",
                        color: "#2f4050",
                        fontWeight: 700,
                      }}
                    >
                      <GetLanguageString props="usra_total" />
                    </td>
                    <td style={{ ...cellStyle, color: "#1ab394", fontWeight: 700 }}>
                      {formatAmount(detail.amount || total)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </Table>
            )}
            {!readOnly && (
              <div style={{ marginTop: 16 }}>
                <label style={{ fontWeight: 600, color: "#2f4050" }}>
                  <GetLanguageString props="notifications_comments" />
                </label>
                <Input
                  type="textarea"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!readOnly && (
          <>
            <Button color="primary" onClick={() => act(1)} disabled={saving || loading || !detail}>
              <GetLanguageString props="notifications_action_approve" />
            </Button>
            <Button
              color="danger"
              onClick={() => act(2)}
              disabled={!comments || saving || loading || !detail}
            >
              <GetLanguageString props="notifications_action_reject" />
            </Button>
          </>
        )}
        <Button color="white" onClick={close}>
          <GetLanguageString props={readOnly ? "common_close" : "common_cancel"} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UsraRemittanceApprovalForm;
